<template>
  <Flex
    direction="column"
    fullWidth
  >
    <Flex
      align="center"
      justify="between"
    >
      <Flex
        direction="column"
        :gap=".25"
      >
        <BaseHeading size="l">
          {{ t('register.salonized_pay.terminal_settings.heading') }}
        </BaseHeading>
        <BaseText color="secondary">
          {{ t('register.salonized_pay.terminal_settings.subheading') }}
        </BaseText>
      </Flex>
      <BaseButton
        :loading="saving"
        @click="save"
        v-test="'sp-terminal-settings-save'"
      >
        {{ t('global.actions.save') }}
      </BaseButton>
    </Flex>

    <BaseAlert
      v-if="errorMessage"
      color="error"
      :text="errorMessage"
      v-test="'sp-terminal-settings-error'"
    />

    <BaseSpinner v-if="loading" />
    <template v-else>
      <TippingCard v-model:presets="presets" />
      <LanguageCard v-model="language" />
    </template>
  </Flex>
</template>

<script
  lang="ts"
  setup
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import config from '@/config';
import { flash } from '@/helpers/ui';
import {
  GET_ADYEN_TERMINAL_SETTINGS,
  UPDATE_TERMINAL_SETTINGS,
} from '../graphql';
import LanguageCard from './terminal-settings/LanguageCard.vue';
import TippingCard from './terminal-settings/TippingCard.vue';
import { type TipPreset, TipType } from './terminal-settings/types';

const parseTipPreset = (raw: string, id: number): TipPreset => ({
  id,
  ...(raw.endsWith('%')
    ? { amount: Number.parseFloat(raw.slice(0, -1)), type: TipType.Perc }
    : { amount: Number.parseInt(raw, 10), type: TipType.Fixed }),
});

const formatTipPreset = (preset: TipPreset): string =>
  preset.type === TipType.Perc
    ? `${preset.amount}%`
    : `${Math.round(preset.amount)}`;

const { t } = useI18n();

const { result, loading, refetch } = useQuery(GET_ADYEN_TERMINAL_SETTINGS);

const adyenStore = computed(() =>
  result.value?.adyenAppState?.adyenStores.find(
    (store: { online: boolean }) => !store.online,
  ),
);

const savedLanguage = computed(
  () => adyenStore.value?.terminalLanguage || config.locales[0],
);

const language = ref<string>(config.locales[0]);

watch(
  savedLanguage,
  (value) => {
    language.value = value;
  },
  { immediate: true },
);

const savedPresets = computed<TipPreset[]>(() =>
  (adyenStore.value?.predefinedTips ?? []).map((raw: string, index: number) =>
    parseTipPreset(raw, index),
  ),
);

const presets = ref<TipPreset[]>([]);

watch(
  savedPresets,
  (value) => {
    presets.value = value.map((preset) => ({ ...preset }));
  },
  { immediate: true },
);

const saving = ref(false);
const errorMessage = ref('');

const { mutate: updateTerminalSettings } = useMutation(
  UPDATE_TERMINAL_SETTINGS,
);

const save = async () => {
  if (!language.value) return;

  saving.value = true;
  errorMessage.value = '';

  try {
    const response = await updateTerminalSettings({
      input: {
        language: language.value,
        tippingAmounts: presets.value
          .filter((preset) => preset.amount !== 0)
          .map(formatTipPreset),
      },
    });

    const errors = response?.data?.updateTerminalSettings?.errors;
    if (errors?.length) {
      errorMessage.value = t('global.error_generic');
      return;
    }

    await refetch();
    flash(t('register.salonized_pay.terminal_settings.flash_updated'));
  } catch {
    errorMessage.value = t('global.error_generic');
  } finally {
    saving.value = false;
  }
};
</script>
